import { useCallback, useState } from 'react'

import IconLoading from '@/assets/icons/icon-loading.svg'
import NotFoundImage from '@/assets/images/not-found.svg'
import type { PostType } from '@/modules/tiktok-research/sounds/types'

import { AddToList } from '../../../ui/AddToList.tsx'
import { VideoCard } from './VideoCard.tsx'

interface Props {
  videos: PostType[]
  lastElementRef: (element: HTMLDivElement) => void
  isFetching?: boolean
}

export const VideoGrid = ({ videos, lastElementRef, isFetching }: Props) => {
  const [selectedVideosMap, setSelectedVideosMap] = useState<
    Map<PostType['id'], boolean>
  >(new Map())

  const handleToggleVideo = useCallback((videoId: PostType['id']) => {
    setSelectedVideosMap((prev) => {
      const newMap = new Map(prev)

      newMap.set(videoId, !prev.get(videoId))

      return newMap
    })
  }, [])

  const selectedVideos = [...selectedVideosMap.entries()]
    .filter(([, value]) => value)
    .map(([key]) => key)

  const resetSelection = () => {
    setSelectedVideosMap(new Map())
  }

  if (videos.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-lg p-4">
        <NotFoundImage
          width={80}
          height={80}
        />
        <p className="text-secondary-300 text-center text-sm font-medium">
          No videos found with selected filters
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {videos.map((video, i, { length }) => (
          <VideoCard
            ref={i === length - 1 ? lastElementRef : undefined}
            item={video}
            key={video.id}
            toggleSelect={handleToggleVideo}
            isSelected={selectedVideosMap.get(video.id) ?? false}
          />
        ))}
      </div>
      {isFetching && (
        <div className="my-3 flex items-center justify-center gap-2">
          <IconLoading className="text-secondary-300 h-4 w-4 animate-spin" />{' '}
          Loading...
        </div>
      )}
      <AddToList
        mediaType="posts"
        selectedItems={selectedVideos}
        onAdd={resetSelection}
        title="Move Videos to List"
        description="Choose the list you want to add the selected videos to or create a new list"
      />
    </>
  )
}
